/**
 * The Micro – Race Gallery
 * Handles: photo manifest loading, category filtering,
 *          load-more pagination, lightbox, and download.
 */
(function () {
  'use strict';

  // ── CONFIG ──────────────────────────────────────────────
  const CLOUD    = 'dfaiiaxym';
  const CDN      = `https://res.cloudinary.com/${CLOUD}/image/upload`;
  const PER_PAGE = 24;   // photos per "Load More" batch

  // Cloudinary named transformations (adjust to taste)
  const T_THUMB  = 'c_fill,g_auto:faces,w_600,h_400,q_auto,f_auto';
  const T_FULL   = 'q_auto,f_auto';
  const T_DL     = 'fl_attachment,q_100';   // forces browser download
  // ────────────────────────────────────────────────────────

  const section  = document.querySelector('.rg-page');
  if (!section) return;

  const manifestUrl  = section.dataset.manifestUrl;
  const grid         = section.querySelector('.js-rg-grid');
  const loadingEl    = section.querySelector('.js-rg-loading');
  const loadMoreBtn  = section.querySelector('.js-rg-load-more');
  const loadedEl     = section.querySelector('.js-rg-loaded');
  const remainingEl  = section.querySelector('.js-rg-remaining');
  const totalEl      = section.querySelector('.js-rg-total');
  const filterBtns   = section.querySelectorAll('.rg-page__filter');

  const lightbox     = document.querySelector('.js-rg-lightbox');
  const lbImg        = lightbox.querySelector('.js-rg-lb-img');
  const lbSpinner    = lightbox.querySelector('.js-rg-lb-spinner');
  const lbDownload   = lightbox.querySelector('.js-rg-lb-download');
  const lbCounter    = lightbox.querySelector('.js-rg-lb-counter');
  const lbClose      = lightbox.querySelector('.js-rg-lb-close');
  const lbPrev       = lightbox.querySelector('.js-rg-lb-prev');
  const lbNext       = lightbox.querySelector('.js-rg-lb-next');
  const lbBackdrop   = lightbox.querySelector('.js-rg-lb-backdrop');

  let allPhotos      = [];
  let filtered       = [];
  let loaded         = 0;
  let lbIndex        = 0;

  // ── UTILITIES ────────────────────────────────────────────

  function url(publicId, transform) {
    return `${CDN}/${transform}/${publicId}`;
  }

  // ── MANIFEST ─────────────────────────────────────────────

  async function loadManifest() {
    if (!manifestUrl) {
      showError('No manifest URL configured. Add it in the theme customizer.');
      return;
    }
    try {
      const res  = await fetch(manifestUrl);
      const data = await res.json();
      allPhotos  = data.photos || [];
      applyFilter('all');
    } catch (e) {
      showError('Could not load photos. Please try refreshing the page.');
      console.error('[RaceGallery] Manifest fetch failed:', e);
    }
  }

  function showError(msg) {
    loadingEl.innerHTML = `<span style="color:#c00">${msg}</span>`;
  }

  // ── FILTERING ────────────────────────────────────────────

  function applyFilter(category) {
    filtered = category === 'all'
      ? [...allPhotos]
      : allPhotos.filter(p => p.category === category);

    loaded = 0;

    // Clear grid (keep loading sentinel out)
    grid.querySelectorAll('.rg-page__item').forEach(el => el.remove());

    if (totalEl) {
      totalEl.textContent = filtered.length.toLocaleString();
    }

    loadingEl.style.display = 'none';
    renderBatch();
  }

  // ── RENDERING ────────────────────────────────────────────

  function renderBatch() {
    const batch = filtered.slice(loaded, loaded + PER_PAGE);

    batch.forEach((photo, i) => {
      const idx  = loaded + i;
      const item = document.createElement('div');
      item.className = 'rg-page__item';

      const thumbUrl = url(photo.id, T_THUMB);

      item.innerHTML = `
        <div class="rg-page__img-wrap">
          <img
            class="rg-page__img"
            src="${thumbUrl}"
            alt="Race photo"
            loading="lazy"
            decoding="async"
          >
          <button
            class="rg-page__zoom"
            data-idx="${idx}"
            aria-label="View full size"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <circle cx="11" cy="11" r="7"/>
              <path d="m21 21-4.35-4.35M11 8v6M8 11h6"/>
            </svg>
          </button>
        </div>`;

      grid.appendChild(item);
    });

    loaded += batch.length;
    updateLoadMore();
  }

  function updateLoadMore() {
    const remaining = filtered.length - loaded;
    if (remaining > 0) {
      loadMoreBtn.hidden   = false;
      loadedEl.textContent    = loaded.toLocaleString();
      remainingEl.textContent = remaining.toLocaleString();
    } else {
      loadMoreBtn.hidden = true;
    }
  }

  // ── LIGHTBOX ─────────────────────────────────────────────

  function openLightbox(idx) {
    lbIndex = idx;
    lightbox.hidden = false;
    document.body.style.overflow = 'hidden';
    showLightboxPhoto();
  }

  function closeLightbox() {
    lightbox.hidden = true;
    document.body.style.overflow = '';
  }

  function showLightboxPhoto() {
    const photo    = filtered[lbIndex];
    const fullUrl  = url(photo.id, T_FULL);
    const dlUrl    = url(photo.id, T_DL);

    // Show spinner while image loads
    lbImg.style.opacity = '0';
    lbSpinner.style.display = 'block';

    lbImg.onload = () => {
      lbImg.style.opacity  = '1';
      lbSpinner.style.display = 'none';
    };

    lbImg.src            = fullUrl;
    lbDownload.href      = dlUrl;
    lbCounter.textContent = `${lbIndex + 1} / ${filtered.length}`;

    lbPrev.style.visibility = lbIndex > 0 ? 'visible' : 'hidden';
    lbNext.style.visibility = lbIndex < filtered.length - 1 ? 'visible' : 'hidden';
  }

  // ── EVENT LISTENERS ──────────────────────────────────────

  // Filter tabs
  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      filterBtns.forEach(b => {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      applyFilter(btn.dataset.category);
    });
  });

  // Open lightbox on zoom button click
  grid.addEventListener('click', e => {
    const btn = e.target.closest('.rg-page__zoom');
    if (btn) openLightbox(parseInt(btn.dataset.idx, 10));
  });

  // Load more
  loadMoreBtn.addEventListener('click', renderBatch);

  // Lightbox controls
  lbClose.addEventListener('click', closeLightbox);
  lbBackdrop.addEventListener('click', closeLightbox);
  lbPrev.addEventListener('click', () => { lbIndex--; showLightboxPhoto(); });
  lbNext.addEventListener('click', () => { lbIndex++; showLightboxPhoto(); });

  // Keyboard navigation
  document.addEventListener('keydown', e => {
    if (lightbox.hidden) return;
    if (e.key === 'Escape')      closeLightbox();
    if (e.key === 'ArrowLeft'  && lbIndex > 0)                        { lbIndex--; showLightboxPhoto(); }
    if (e.key === 'ArrowRight' && lbIndex < filtered.length - 1)     { lbIndex++; showLightboxPhoto(); }
  });

  // Touch swipe support for lightbox
  let touchStartX = 0;
  lightbox.addEventListener('touchstart', e => {
    touchStartX = e.changedTouches[0].clientX;
  }, { passive: true });
  lightbox.addEventListener('touchend', e => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) < 50) return;
    if (dx < 0 && lbIndex < filtered.length - 1) { lbIndex++; showLightboxPhoto(); }
    if (dx > 0 && lbIndex > 0)                   { lbIndex--; showLightboxPhoto(); }
  }, { passive: true });

  // ── INIT ─────────────────────────────────────────────────

  loadManifest();

})();